// HM advance/hold/pass (`#requisitions/<R>/batches/<n>`, the V1 route the HM's dated review task links to; DS §1.10,
// P-8…P-14). It replaces V1's batch page. The Hiring Manager of the requisition decides each candidate of the slate —
// **Advance**, or **Hold** / **Pass** with the configured reason — and **Submit** sends every decision in one act
// (`candidate.batch_decide`), blocked until each is ready: "Require reason for Hold and Pass." · "Pass cannot finish
// without disposition reason." Opening the slate records the response time (`candidate.batch_open`, first view only).
// Tabs *Pending Review · Advanced · Held · Passed*; a held row shows its age and reason ("Held remains active with age
// and reason."). The Recruiter and the HR Business Partner read the same rows with no control ("TA leadership/HRBP see
// authorized status metrics."); an Interviewer is told "Interviewers have no access at this stage." Each row links the
// résumé version the slate carried (*Original resume*), never a later upload.
// The header reads the slate's chain word (a decided slate is *Completed*, UAT D-22) with a meta line — the requisition,
// the submission and the completion, or the due date while it is open — over a KPI strip of the four review statuses,
// counted from the slate's own rows so every seat that reads the slate sees them; an empty tab reads his sentence for
// that state, and an empty Pending Review reads *Nothing needs action* rather than restating the slate requirement
// (UAT F-32, Q-4's default).
// Hosted by MissionShell: views/b3/register.ts registers it through S12's registerView, so it draws no McRoot of its own.

import { useEffect, useRef, useState } from "react";
import type { ApplicationCard, CandidateSlateOpenOutput, HrbpApplicationCard } from "@/api/tools";
import { candidateBatchDecide, candidateBatchOpen, candidateList, candidateSlateOpen } from "@/api/tools";
import { isClientRefusal, useWrite } from "@/api/useTool";
import { go, useRoute } from "@/router";
import { useSession } from "@/session/useSession";
import { useViewState } from "@/shell/viewState";
import { NotFound } from "@/views/NotFound";
import {
  ACTION_LABELS, Button, Card, CORE_LABELS, Due, EmptyState, ErrorStrip, Flex, Kpi, KpiRow, METRIC_TEMPLATES, HM_REVIEW_STATUS, NAV_ITEM_LABELS,
  PageHeader, Pill, Quote, RULE_LABELS, Recorded, SLATE_STATUS, Skeleton, Stack, Tabs, TextLink, formatDateTime,
} from "@/mc";
import { useHoldReasonChoices, usePassReasonChoices, useSourceWord } from "@/views/candidate/options";
import { queryOf, useSettledRead, withQuery } from "@/views/shared/settledRead";
import { DecisionControls, OriginalResume } from "./Decision";
import { DecisionSlideOver } from "./DecisionSlideOver";
import { PIPELINE_LABELS, PIPELINE_RULES } from "./labels";
import {
  EMPTY_DECISION, REVIEW_TABS, REVIEW_TAB_WORD, decisionPayload, dueTierOf, nameOf, reviewTab, submitBlock, type DecisionDraft, type ReviewTab,
} from "./pipeline";
import { pipelineRefusal } from "./refusals";

const SLATE_WORD: Readonly<Record<string, string>> = {
  draft: SLATE_STATUS.draftBatch, submitted: SLATE_STATUS.submitted, in_hm_review: SLATE_STATUS.inHmReview,
  completed: SLATE_STATUS.completed, overdue: SLATE_STATUS.overdue,
};

/** An empty tab's line: his sentence for that state (Pending Review: the Q-E15 line), never the bare status word. */
export const REVIEW_EMPTY: Readonly<Record<ReviewTab, string>> = {
  pendingReview: CORE_LABELS.nothingNeedsAction,
  advanced: PIPELINE_RULES.retainAdvanceHoldPass,
  held: PIPELINE_RULES.heldAge,
  passed: PIPELINE_RULES.passNeedsReason,
};

/** *HM advance/hold/pass* with the slate's chain word and its meta line: the requisition, then the submission and the
 *  completion — or, while the slate is open, its due date. */
export function SlateHeader({ rid, slate: s, title, loading }: { rid: string; slate: CandidateSlateOpenOutput | undefined; title: string | undefined; loading: boolean }) {
  return (
    <PageHeader
      title={PIPELINE_LABELS.hmAdvanceHoldPass}
      loading={loading}
      pills={s ? <Pill tone={s.state === "overdue" ? "red" : s.state === "completed" ? "ok" : "info"}>{SLATE_WORD[s.state] ?? s.state_label}</Pill> : undefined}
      meta={s ? [
        <span key="r" className="mc-mono">{rid}</span>,
        ...(title ? [<span key="t">{title}</span>] : []),
        ...(s.submitted_at ? [<span key="s">{`${SLATE_STATUS.submitted} · ${formatDateTime(s.submitted_at)}`}</span>] : []),
        ...(s.completed_at ? [<span key="c">{`${SLATE_STATUS.completed} · ${formatDateTime(s.completed_at)}`}</span>] : []),
        ...(s.due_at && s.state !== "completed" ? [<Due key="d" tier={dueTierOf(s)} phrase={formatDateTime(s.due_at)} at={s.due_at}  />] : []),
      ] : undefined}
    />
  );
}

/** The KPI strip: how many of the slate's candidates stand in each review status ("Pending Review -> Advanced/Held/Passed"). */
export function SlateKpis({ rows }: { rows: readonly { stage: string }[] }) {
  return (
    <KpiRow>
      {REVIEW_TABS.map((key) => <Kpi key={key} label={REVIEW_TAB_WORD[key]} value={rows.filter((row) => reviewTab(row) === key).length} />)}
    </KpiRow>
  );
}

export function HmReviewPage({ rid, batchNo }: { rid: string; batchNo: number }) {
  return <ReviewBody rid={rid} batchNo={batchNo} />;
}

function ReviewBody({ rid, batchNo }: { rid: string; batchNo: number }) {
  const route = useRoute();
  const { can } = useSession();
  const { register } = useViewState();
  const allowed = can("candidate.slate_open");
  const slate = useSettledRead(["slate", rid, batchNo], () => candidateSlateOpen({ requisition_id: rid, batch_no: batchNo }), { enabled: allowed });
  const list = useSettledRead(["candidates", rid, "batch", batchNo], () => candidateList({ requisition_id: rid, batch_no: batchNo }), { enabled: allowed && can("candidate.list") });
  useEffect(() => register({ view: "batch", ids: { requisition: rid, batch: String(batchNo) }, summary: { state: slate.data?.state ?? "loading" } }), [register, rid, batchNo, slate.data?.state]);
  if (!allowed) return <EmptyState icon="lock">{RULE_LABELS.interviewersNoAccess}</EmptyState>;
  if (slate.error && isClientRefusal(slate.error)) return <NotFound route={route.raw} />;
  const s = slate.data;
  return (
    <Stack gap={16}>
      <Flex gap={8}><TextLink href={`#pipeline/${rid}`} >{NAV_ITEM_LABELS.pipeline}</TextLink></Flex>
      <SlateHeader rid={rid} slate={s} title={list.data?.requisition_title} loading={slate.isLoading} />
      {s ? <SlateKpis rows={s.rows} /> : null}
      {slate.error ? (
        <ErrorStrip message={RULE_LABELS.failedSyncRetryable} problem={{ status: slate.error.status, refusal_code: slate.error.code, request_id: slate.error.requestId }} onRetry={() => void slate.refetch()}  />
      ) : null}
      {!s ? (slate.error ? null : <Skeleton lines={6} height={18} />) : <Review rid={rid} batchNo={batchNo} slate={s} items={list.data?.items ?? null} projection={list.data?.projection ?? s.projection} live={list.data ? list.data.state_archive === "live" : false} />}
    </Stack>
  );
}

type Item = ApplicationCard | HrbpApplicationCard;

function Review({ rid, batchNo, slate, items, projection, live }: {
  rid: string; batchNo: number; slate: CandidateSlateOpenOutput; items: readonly Item[] | null; projection: string; live: boolean;
}) {
  const route = useRoute();
  const flags = queryOf(route.raw);
  const { can } = useSession();
  const decides = live && projection === "review" && can("candidate.batch_decide");
  const inval = [["pipeline"], ["candidates", rid], ["slate", rid], ["application"], ["requisition", rid], ["today"]] as const;
  const opened = useWrite((input: { requisition_id: string; batch_no: number }, key) => candidateBatchOpen(input, key), inval);
  const decide = useWrite((input: Parameters<typeof candidateBatchDecide>[0], key) => candidateBatchDecide(input, key), inval);
  const [drafts, setDrafts] = useState<Record<string, DecisionDraft>>({});
  const [refused, setRefused] = useState<string | null>(null);
  const [tab, setTab] = useState<ReviewTab>("pendingReview");
  const asked = useRef(false);
  const rows = items ?? [];
  const pending = rows.filter((row) => row.stage === "in_review").map((row) => row.id);

  // the first view records the response time ("Record submission and response timestamps"); later views add nothing
  useEffect(() => {
    if (!decides || asked.current || slate.opened_at || pending.length === 0) return;
    asked.current = true;
    opened.mutateAsync({ requisition_id: rid, batch_no: batchNo }).catch(() => undefined);
  }, [decides, slate.opened_at, pending.length, rid, batchNo, opened]);

  const why = submitBlock(pending, drafts);
  const submit = () => {
    setRefused(null);
    decide
      .mutateAsync({
        requisition_id: rid,
        batch_no: batchNo,
        decisions: pending.map((id) => ({ ...decisionPayload(id, drafts[id] ?? EMPTY_DECISION) })),
      })
      .then(() => go(`#pipeline/${rid}`))
      .catch((error: unknown) => setRefused(pipelineRefusal(error) ?? RULE_LABELS.failedSyncRetryable));
  };
  const counts = Object.fromEntries(REVIEW_TABS.map((key) => [key, rows.filter((row) => reviewTab(row) === key).length])) as Record<ReviewTab, number>;
  const shown = rows.filter((row) => reviewTab(row) === tab);
  const deciding = flags.get("decide");
  const openedAt = slate.opened_at ?? (opened.isSuccess ? new Date().toISOString() : null);

  return (
    <Stack gap={12}>
      {openedAt ? <Recorded right={formatDateTime(openedAt)} /> : null}
      <Tabs
        label={PIPELINE_LABELS.hmAdvanceHoldPass}
        items={REVIEW_TABS.map((key) => ({ key, label: REVIEW_TAB_WORD[key], count: counts[key] }))}
        active={tab}
        onSelect={(key) => setTab(key as ReviewTab)}
      />
      <Card refused={refused ? { reason: refused } : undefined} sub={tab === "held" && shown.length > 0 ? PIPELINE_RULES.heldAge : undefined}>
        {items === null ? (
          <Skeleton lines={4} height={18} />
        ) : shown.length === 0 ? (
          <EmptyState inline>{REVIEW_EMPTY[tab]}</EmptyState>
        ) : (
          <Stack gap={16}>
            {shown.map((row) => (
              <ReviewRow
                key={row.id}
                rid={rid}
                row={row}
                index={rows.indexOf(row)}
                draft={decides && row.stage === "in_review" ? (drafts[row.id] ?? EMPTY_DECISION) : null}
                onDraft={(draft) => setDrafts({ ...drafts, [row.id]: draft })}
                busy={decide.isPending}
                onOpen={decides && row.stage === "in_review" ? () => go(withQuery(`#requisitions/${rid}/batches/${batchNo}`, { decide: row.id }, flags)) : undefined}
              />
            ))}
          </Stack>
        )}
      </Card>
      {decides && pending.length > 0 ? (
        <Flex justify="end">
          <Button tone="primary" blocked={why ? { reason: why } : undefined} busy={decide.isPending} onClick={submit} >
            {ACTION_LABELS.submit}
          </Button>
        </Flex>
      ) : null}
      {deciding && decides ? (
        <DecisionSlideOver
          rid={rid}
          card={rows.find((row): row is ApplicationCard => row.id === deciding && "recruiter_note" in row) ?? null}
          slate={slate}
          onClose={() => go(withQuery(`#requisitions/${rid}/batches/${batchNo}`, { decide: null }, flags))}
        />
      ) : null}
    </Stack>
  );
}

function ReviewRow({ rid, row, index, draft, onDraft, busy, onOpen }: {
  rid: string; row: Item; index: number; draft: DecisionDraft | null; onDraft: (draft: DecisionDraft) => void; busy: boolean;
  onOpen?: (() => void) | undefined;
}) {
  const sourceWord = useSourceWord();
  const holdReasons = useHoldReasonChoices();
  const passReasons = usePassReasonChoices();
  const named = "recruiter_note" in row;
  const last = named ? row.last_decision : null;
  return (
    <div role="group" aria-label={nameOf(row)}>
    <Stack gap={8}>
      <Flex gap={8} wrap align="center">
        {onOpen ? <TextLink onClick={onOpen} >{nameOf(row)}</TextLink> : <b>{nameOf(row)}</b>}
        {named ? <Pill tone="neutral">{sourceWord(row.source_channel)}</Pill> : null}
        {row.stage === "held" ? (
          <Pill tone="warn">{[HM_REVIEW_STATUS.held, row.held_days != null ? METRIC_TEMPLATES.nDays({ n: row.held_days }) : null, named ? row.hold_reason_label : null].filter(Boolean).join(" · ")}</Pill>
        ) : (
          <Pill tone={reviewTab(row) === "pendingReview" ? "info" : reviewTab(row) === "advanced" ? "ok" : "neutral"}>{REVIEW_TAB_WORD[reviewTab(row)]}</Pill>
        )}
        {named ? <OriginalResume rid={rid} card={row}  /> : null}
      </Flex>
      {named && row.recruiter_note ? <Quote>{row.recruiter_note}</Quote> : null}
      {draft ? <DecisionControls draft={draft} onChange={onDraft} holdReasons={holdReasons} passReasons={passReasons} scope={`review.${index}`} busy={busy} /> : null}
      {!draft && last && row.stage !== "in_review" && typeof last["at"] === "string" ? (
        <time dateTime={last["at"]}>{formatDateTime(last["at"])}</time>
      ) : null}
    </Stack>
    </div>
  );
}
